import React, { useState, useEffect } from 'react';
import Layout from '../../components/admin/Layout';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import './DataTable.css';
import './Roles.css';

export default function Roles() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('roles.manage');

  const [roles,       setRoles]       = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading,     setLoading]     = useState(true);
  const [modalOpen,   setModalOpen]   = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [saving,      setSaving]      = useState(false);

  const [form, setForm] = useState({ name:'', description:'', permissions:[] });
  const [errors, setErrors] = useState({});

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/admin/roles'),
      api.get('/admin/roles/permissions'),
    ]).then(([r1, r2]) => {
      setRoles(r1.data || []);
      setPermissions(r2.data || {});
    }).catch(() => showToast('Failed to load roles', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [showToast]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name:'', description:'', permissions:[] });
    setErrors({});
    setModalOpen(true);
  };

  const openEdit = async (role) => {
    try {
      const r = await api.get(`/admin/roles/${role.id}`);
      setEditing(role);
      setForm({
        name: r.data.name,
        description: r.data.description || '',
        permissions: r.data.permissions || [],
      });
      setErrors({});
      setModalOpen(true);
    } catch {
      showToast('Failed to load role', 'error');
    }
  };

  const togglePerm = (id) =>
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(id)
        ? f.permissions.filter(p => p !== id)
        : [...f.permissions, id],
    }));

  const toggleModule = (modulePerms) => {
    const ids = modulePerms.map(p => p.id);
    const allSelected = ids.every(id => form.permissions.includes(id));
    setForm(f => ({
      ...f,
      permissions: allSelected
        ? f.permissions.filter(p => !ids.includes(p))
        : [...new Set([...f.permissions, ...ids])],
    }));
  };

  const save = async () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Role name is required.';
    if (form.permissions.length === 0) e.permissions = 'Select at least one permission.';
    setErrors(e);
    if (Object.keys(e).length) return;

    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/roles/${editing.id}`, form);
        showToast('Role updated', 'success');
      } else {
        await api.post('/admin/roles', form);
        showToast('Role created', 'success');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save role', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (role) => {
    if (!window.confirm(`Delete the "${role.name}" role? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/roles/${role.id}`);
      showToast('Role deleted', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to delete role', 'error');
    }
  };

  return (
    <Layout>
      <div className="data-table-page">
        <div className="page-header">
          <div>
            <h1>Roles &amp; Permissions</h1>
            <p>Define what each admin role can access and manage.</p>
          </div>
          {canManage && (
            <button className="btn btn-primary" onClick={openCreate}>+ New Role</button>
          )}
        </div>

        {loading ? (
          <div className="table-loading"><div className="spinner" /></div>
        ) : (
          <div className="roles-grid">
            {roles.map(role => (
              <div className="role-card" key={role.id}>
                <div className="role-card-header">
                  <div>
                    <h3>{role.name}</h3>
                    {role.is_system ? <span className="badge badge-gray">System</span> : null}
                  </div>
                  {canManage && (
                    <div className="action-btns">
                      <button className="btn-xs btn-xs-outline" onClick={() => openEdit(role)}>Edit</button>
                      {!role.is_system && (
                        <button className="btn-xs btn-xs-danger" onClick={() => remove(role)}>Delete</button>
                      )}
                    </div>
                  )}
                </div>
                {role.description && <p className="role-desc">{role.description}</p>}
                <div className="role-stats">
                  <span><strong>{role.permission_count || 0}</strong> permissions</span>
                  <span><strong>{role.user_count || 0}</strong> users</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {modalOpen && (
          <div className="modal-overlay" onClick={() => setModalOpen(false)}>
            <div className="modal-box modal-lg" onClick={e => e.stopPropagation()}>
              <h3>{editing ? `Edit Role: ${editing.name}` : 'Create New Role'}</h3>

              <div className="form-group">
                <label className="form-label">Role Name <span className="req">*</span></label>
                <input
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Event Manager"
                  disabled={editing?.is_system}
                />
                {errors.name && <span className="invalid-feedback">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  className="form-control"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Brief description of this role"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Permissions <span className="req">*</span></label>
                {errors.permissions && <span className="invalid-feedback" style={{display:'block', marginBottom:'0.5rem'}}>{errors.permissions}</span>}
                <div className="perm-modules">
                  {Object.entries(permissions).map(([module, perms]) => {
                    const ids = perms.map(p => p.id);
                    const allSelected = ids.every(id => form.permissions.includes(id));
                    const someSelected = ids.some(id => form.permissions.includes(id));
                    return (
                      <div className="perm-module" key={module}>
                        <div className="perm-module-header">
                          <label className="perm-checkbox module-check">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                              onChange={() => toggleModule(perms)}
                            />
                            <span className="module-name">{module.replace(/_/g, ' ')}</span>
                          </label>
                        </div>
                        <div className="perm-items">
                          {perms.map(p => (
                            <label className="perm-checkbox" key={p.id}>
                              <input
                                type="checkbox"
                                checked={form.permissions.includes(p.id)}
                                onChange={() => togglePerm(p.id)}
                              />
                              <span>{p.action} <span className="perm-desc">{p.description}</span></span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="modal-actions">
                <button className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={save} disabled={saving}>
                  {saving ? <><span className="spinner spinner-sm" /> Saving…</> : editing ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
